// scene-core/voxel-walk.ts — walking on a voxel collision grid. Shared by the
// editor walk preview and viewer2, like walk-geometry.ts.
//
// Division of labour when a scene has voxels:
//   - the walk planes, boundaries and stairs (walk-geometry surfaces) decide
//     WHERE you may walk and HOW HIGH: XZ and the floor height come from
//     resolveWalkPoint exactly as in a scene without voxels;
//   - the voxels only decide what is in the way: a standing body (a vertical
//     cylinder from step height above the plane to the top of the head) may
//     not overlap occupied voxels, so the camera stops at objects and walls
//     instead of passing through them.
//
// World = the editor/viewer world (Y up). The grid lives in its own voxel
// frame; `VoxelFrame` maps between the two with the scene.json Transform of
// the voxels (position, Euler XYZ degrees as PlayCanvas applies them, scale).

import type { VoxelGrid } from './voxel-grid';
import {
    STEP_TOLERANCE, resolveWalkPoint, seedWalkPoint,
    type WalkSurface
} from './walk-geometry';

export interface TransformLike {
    position?: number[];
    rotation?: number[];   // Euler degrees, XYZ, PlayCanvas convention
    scale?: number[];
}

/**
 * Column-major 3x3 rotation of PlayCanvas `Mat4.setFromEulerAngles` (the
 * matrix an entity gets from `setLocalEulerAngles(ex, ey, ez)`).
 */
export const eulerToMat3 = (ex: number, ey: number, ez: number): number[] => {
    const d = Math.PI / 180;
    const s1 = Math.sin(-ex * d), c1 = Math.cos(-ex * d);
    const s2 = Math.sin(-ey * d), c2 = Math.cos(-ey * d);
    const s3 = Math.sin(-ez * d), c3 = Math.cos(-ez * d);
    return [
        c2 * c3, -c2 * s3, s2,
        c1 * s3 + c3 * s1 * s2, c1 * c3 - s1 * s2 * s3, -c2 * s1,
        s1 * s3 - c1 * c3 * s2, c3 * s1 + c1 * s2 * s3, c1 * c2
    ];
};

/** world = position + R · (scale ∘ voxel), and its inverse. */
export class VoxelFrame {
    private readonly m: number[];   // column-major rotation
    private readonly t: [number, number, number];
    private readonly s: [number, number, number];

    constructor(transform?: TransformLike) {
        const r = transform?.rotation ?? [0, 0, 0];
        const p = transform?.position ?? [0, 0, 0];
        const s = transform?.scale ?? [1, 1, 1];
        this.m = eulerToMat3(r[0] || 0, r[1] || 0, r[2] || 0);
        this.t = [p[0] || 0, p[1] || 0, p[2] || 0];
        this.s = [s[0] || 1, s[1] || 1, s[2] || 1];
    }

    toWorld(x: number, y: number, z: number, out: number[] = [0, 0, 0]): number[] {
        const m = this.m;
        x *= this.s[0]; y *= this.s[1]; z *= this.s[2];
        out[0] = m[0] * x + m[3] * y + m[6] * z + this.t[0];
        out[1] = m[1] * x + m[4] * y + m[7] * z + this.t[1];
        out[2] = m[2] * x + m[5] * y + m[8] * z + this.t[2];
        return out;
    }

    toVoxel(x: number, y: number, z: number, out: number[] = [0, 0, 0]): number[] {
        const m = this.m;
        x -= this.t[0]; y -= this.t[1]; z -= this.t[2];
        // R^T for the inverse rotation
        out[0] = (m[0] * x + m[1] * y + m[2] * z) / this.s[0];
        out[1] = (m[3] * x + m[4] * y + m[5] * z) / this.s[1];
        out[2] = (m[6] * x + m[7] * y + m[8] * z) / this.s[2];
        return out;
    }
}

export interface VoxelColliderOptions {
    /** Body radius (metres). Default 0.15: on the Damm camera path 0.2 m
     *  flagged 5% of the real camera positions as touching voxels, 0.15 m 3%. */
    radius?: number;
    /** Highest ledge the feet step onto without stairs. Default STEP_TOLERANCE. */
    stepHeight?: number;
    /** Body top above the eye (top of the head). Default 0.1. */
    headroom?: number;
    /** Below this height above the floor only the legs (half the radius)
     *  count, so the next stair tread doesn't catch the body's rim on steep
     *  stairs. Default 0.7. */
    kneeHeight?: number;
}

// Where the body is sampled around its axis, as fractions of the radius: the
// rim in 16 directions (a 5 cm pole can't slip between them) and the legs,
// an inner ring in 8 plus the axis.
const RIM: [number, number][] = [];
for (let i = 0; i < 16; i++) RIM.push([Math.cos(i * Math.PI / 8), Math.sin(i * Math.PI / 8)]);
const LEGS: [number, number][] = [[0, 0]];
for (let i = 0; i < 8; i++) LEGS.push([0.5 * Math.cos(i * Math.PI / 4 + Math.PI / 8), 0.5 * Math.sin(i * Math.PI / 4 + Math.PI / 8)]);

export class VoxelCollider {
    readonly grid: VoxelGrid;
    readonly frame: VoxelFrame;
    readonly radius: number;
    readonly stepHeight: number;
    readonly headroom: number;
    readonly kneeHeight: number;
    /** Vertical sampling step in world units: half a voxel, so a tilted frame never skips one. */
    private readonly dy: number;
    private readonly tmp: number[] = [0, 0, 0];

    constructor(grid: VoxelGrid, transform?: TransformLike, opts: VoxelColliderOptions = {}) {
        this.grid = grid;
        this.frame = new VoxelFrame(transform);
        const sc = transform?.scale?.[0] ?? 1;
        this.dy = grid.res * Math.abs(sc || 1) * 0.5;
        this.radius = opts.radius ?? 0.15;
        this.stepHeight = opts.stepHeight ?? STEP_TOLERANCE;
        this.headroom = opts.headroom ?? 0.1;
        this.kneeHeight = opts.kneeHeight ?? 0.7;
    }

    /** Is the world point inside an occupied voxel? */
    solidAt(x: number, y: number, z: number): boolean {
        const v = this.frame.toVoxel(x, y, z, this.tmp);
        return this.grid.isSolidAt(v[0], v[1], v[2]);
    }

    /**
     * Floor height under (x, z) near `feetY`: the top of the highest occupied
     * voxel whose top lies in [feetY - down, feetY + up] with empty space
     * right above it. NaN when the band holds no such top (a hole, or a wall
     * filling the whole band — columnEmpty tells the two apart).
     */
    floorAt(x: number, z: number, feetY: number, up = this.stepHeight, down = this.stepHeight): number {
        const dy = this.dy;
        let above = this.solidAt(x, feetY + up + dy, z);
        for (let y = feetY + up; y >= feetY - down - dy; y -= dy) {
            const here = this.solidAt(x, y, z);
            if (here && !above) {
                // refine the empty→solid boundary between y and y + dy
                let lo = y, hi = y + dy;
                for (let k = 0; k < 6; k++) {
                    const mid = (lo + hi) / 2;
                    if (this.solidAt(x, mid, z)) lo = mid; else hi = mid;
                }
                const top = (lo + hi) / 2;
                if (top <= feetY + up && top >= feetY - down) return top;
            }
            above = here;
        }
        return NaN;
    }

    /** No occupied voxel in the column at (x, z) between y0 and y1 (a hole in the data). */
    columnEmpty(x: number, z: number, y0: number, y1: number): boolean {
        for (let y = y0; y <= y1; y += this.dy) {
            if (this.solidAt(x, y, z)) return false;
        }
        return true;
    }

    /**
     * Distance along a world ray (unit `rd`) to the first occupied voxel, or
     * NaN within `maxDist`. Marches half a voxel at a time, then refines.
     */
    raycast(ro: number[], rd: number[], maxDist: number): number {
        const dt = this.dy;
        for (let t = 0; t <= maxDist; t += dt) {
            if (!this.solidAt(ro[0] + rd[0] * t, ro[1] + rd[1] * t, ro[2] + rd[2] * t)) continue;
            let lo = Math.max(0, t - dt), hi = t;
            for (let k = 0; k < 6; k++) {
                const mid = (lo + hi) / 2;
                if (this.solidAt(ro[0] + rd[0] * mid, ro[1] + rd[1] * mid, ro[2] + rd[2] * mid)) hi = mid; else lo = mid;
            }
            return hi;
        }
        return NaN;
    }

    /**
     * Outward surface direction at a world point on the voxels (unit, world
     * frame): the sum of the directions to the empty cells within `reach`
     * voxels around it (2 smooths out the 5 cm stair-stepping of the grid).
     * Zero vector when the point is buried or in the open.
     */
    normalAt(x: number, y: number, z: number, out: number[] = [0, 0, 0], reach = 2): number[] {
        const h = this.dy * 2;
        let nx = 0, ny = 0, nz = 0;
        for (let i = -reach; i <= reach; i++) {
            for (let j = -reach; j <= reach; j++) {
                for (let k = -reach; k <= reach; k++) {
                    if ((i || j || k) && !this.solidAt(x + i * h, y + j * h, z + k * h)) {
                        nx += i; ny += j; nz += k;
                    }
                }
            }
        }
        const l = Math.hypot(nx, ny, nz);
        out[0] = l ? nx / l : 0; out[1] = l ? ny / l : 0; out[2] = l ? nz / l : 0;
        return out;
    }

    /**
     * Does a body standing on `floorY` at (x, z) overlap occupied voxels?
     * The body starts at step height above the floor (anything lower is
     * stepped onto) and ends at the top of the head; below the knee only the
     * legs (half the radius) count, above it the full `radius` rim.
     * `legs` false skips the leg zone (authored stairs: the next treads sit
     * right in front of the feet, and the voxels smear them together).
     */
    bodyBlocked(x: number, z: number, floorY: number, eyeHeight: number, legs = true): boolean {
        const y0 = floorY + (legs ? this.stepHeight : this.kneeHeight) + this.dy;
        const y1 = floorY + eyeHeight + this.headroom;
        const knee = floorY + this.kneeHeight;
        const r = this.radius;
        for (let y = y0; y <= y1; y += this.dy * 2) {
            for (const [cx, cz] of LEGS) {
                if (this.solidAt(x + cx * r, y, z + cz * r)) return true;
            }
            if (y < knee) continue;
            for (const [cx, cz] of RIM) {
                if (this.solidAt(x + cx * r, y, z + cz * r)) return true;
            }
        }
        return false;
    }
}

/** A standing position with voxel collision: XZ and the walk-surface floor height. */
export interface VoxelWalkPoint {
    x: number;
    z: number;
    /** FLOOR height (feet) of the walk surface under (x, z). */
    y: number;
    /** Same as `y`: the walk-surface height (kept for callers that read it). */
    planeY: number;
    /** Index into the surfaces array (-1 if none). */
    surface: number;
}

/** Longest XZ move tested in one go (under one 5 cm voxel); longer moves are
 *  split so the body's rim meets a wall before its centre could skip it. */
const MAX_SUBSTEP = 0.04;
/** Cap on substeps per resolve (~2.5 m); the rest of a longer jump is dropped. */
const MAX_SUBSTEPS = 64;

/**
 * Per-frame movement with voxel collision. `from` is the current standing
 * point (from a previous resolve or seedVoxelWalkPoint); (x, z) the wished-for
 * XZ. The move goes through the walk surfaces in short substeps; each needs
 * a free body at the surface height there. A blocked substep tries sliding
 * along X, then along Z. A body that already overlaps voxels (spawned in a
 * floater) may still move, so a bad spawn never traps the camera.
 */
export const resolveVoxelWalkPoint = (
    collider: VoxelCollider,
    surfaces: WalkSurface[],
    eyeHeight: number,
    from: VoxelWalkPoint,
    x: number,
    z: number
): VoxelWalkPoint => {
    // Each substep heads from where the body actually is (after any slide)
    // toward the target, never more than MAX_SUBSTEP.
    let cur = from;
    for (let i = 0; i < MAX_SUBSTEPS; i++) {
        const dx = x - cur.x, dz = z - cur.z;
        const d = Math.hypot(dx, dz);
        if (d < 1e-6) break;
        const k = d > MAX_SUBSTEP ? MAX_SUBSTEP / d : 1;
        const next = voxelStep(collider, surfaces, eyeHeight, cur, cur.x + dx * k, cur.z + dz * k);
        if (next === cur) break;   // fully blocked
        cur = next;
    }
    return cur;
};

const voxelStep = (
    collider: VoxelCollider,
    surfaces: WalkSurface[],
    eyeHeight: number,
    cur: VoxelWalkPoint,
    x: number,
    z: number
): VoxelWalkPoint => {
    const stuck = collider.bodyBlocked(cur.x, cur.z, cur.y, eyeHeight);
    const tryAt = (tx: number, tz: number): VoxelWalkPoint | null => {
        const g = surfaces.length
            ? resolveWalkPoint(surfaces, cur.y, tx, tz)
            : { x: tx, z: tz, y: cur.y, surface: -1 };
        if (g.x === cur.x && g.z === cur.z) return null;
        // the gate re-seeds anywhere when nothing is in reach: never teleport
        if (Math.hypot(g.x - tx, g.z - tz) > 2 * MAX_SUBSTEP) return null;
        const onStairs = g.surface >= 0 && surfaces[g.surface]?.kind === 'stairs';
        if (!stuck && collider.bodyBlocked(g.x, g.z, g.y, eyeHeight, !onStairs)) return null;
        return { x: g.x, z: g.z, y: g.y, planeY: g.y, surface: g.surface };
    };
    return tryAt(x, z) ??
        (x !== cur.x ? tryAt(x, cur.z) : null) ??
        (z !== cur.z ? tryAt(cur.x, z) : null) ??
        cur;
};

/**
 * Place the camera with no valid previous floor (spawn, teleport): the walk
 * surfaces pick XZ, the storey and the height (seedWalkPoint).
 */
export const seedVoxelWalkPoint = (
    _collider: VoxelCollider,   // the surfaces alone place the spawn; kept for callers
    surfaces: WalkSurface[],
    x: number,
    z: number,
    preferY: number
): VoxelWalkPoint => {
    const s = seedWalkPoint(surfaces, x, z, preferY);
    const y = Number.isFinite(s.y) ? s.y : (Number.isFinite(preferY) ? preferY : 0);
    return { x: s.x, z: s.z, y, planeY: y, surface: s.surface };
};
